import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { CustomerTicket, TicketResponse } from '../types/customerTicket';

const COLLECTION_NAME = 'customerTickets';

class CustomerTicketService {
  // Create a new support ticket
  async createTicket(data: Omit<CustomerTicket, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      // Remove undefined values (Firestore doesn't support them)
      const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...cleanData,
        responses: [],
        attachments: cleanData.attachments || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }
  }

  // Get all projects for an organization
  async getProjectsByOrganization(organizationId: string): Promise<CustomerTicket[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocToProject(doc));
    } catch (error) {
      console.error('Error getting projects by organization:', error);
      throw error;
    }
  }

  // Get all projects for a customer
  async getProjectsByCustomer(customerId: string): Promise<CustomerTicket[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocToProject(doc));
    } catch (error) {
      console.error('Error getting projects by customer:', error);
      throw error;
    }
  }

  // Get active projects for a customer (not completed or cancelled)
  async getActiveProjectsByCustomer(customerId: string): Promise<CustomerTicket[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('status', 'in', ['submitted', 'in_progress', 'waiting_customer']),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocToProject(doc));
    } catch (error) {
      console.error('Error getting active projects:', error);
      throw error;
    }
  }

  // Get a single project by ID
  async getProjectById(projectId: string): Promise<CustomerTicket | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, projectId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return this.mapDocToProject(docSnap);
      }
      return null;
    } catch (error) {
      console.error('Error getting project by ID:', error);
      throw error;
    }
  }

  // Update project
  async updateProject(projectId: string, data: Partial<CustomerTicket>): Promise<void> {
    try {
      // Remove undefined values (Firestore doesn't support them)
      const cleanData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const projectRef = doc(db, COLLECTION_NAME, projectId);
      await updateDoc(projectRef, {
        ...cleanData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  }

  // Add response to project
  async addResponse(projectId: string, response: Omit<TicketResponse, 'id' | 'createdAt'>): Promise<void> {
    try {
      const project = await this.getProjectById(projectId);
      if (!project) throw new Error('Project not found');

      const newResponse: TicketResponse = {
        ...response,
        id: `response_${Date.now()}`,
        createdAt: new Date()
      };

      const responses = [...(project.responses || []), newResponse];
      
      await this.updateProject(projectId, { responses });
    } catch (error) {
      console.error('Error adding response:', error);
      throw error;
    }
  }

  // Update project status
  async updateStatus(projectId: string, status: CustomerTicket['status'], userId: string): Promise<void> {
    try {
      const updateData: any = {
        status,
        updatedAt: serverTimestamp()
      };

      if (status === 'completed') {
        updateData.completedAt = serverTimestamp();
      } else if (status === 'cancelled') {
        updateData.cancelledAt = serverTimestamp();
      }

      const projectRef = doc(db, COLLECTION_NAME, projectId);
      await updateDoc(projectRef, updateData);
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  }

  // Assign project to user
  async assignProject(projectId: string, userId: string, userName: string): Promise<void> {
    try {
      await this.updateProject(projectId, {
        assignedTo: userId,
        assignedToName: userName
      });
    } catch (error) {
      console.error('Error assigning project:', error);
      throw error;
    }
  }

  // Delete project
  async deleteProject(projectId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }

  // Add attachment to ticket
  async addAttachment(ticketId: string, attachment: any): Promise<void> {
    try {
      const ticket = await this.getProjectById(ticketId);
      if (!ticket) throw new Error('Ticket not found');

      const attachments = [...(ticket.attachments || []), attachment];
      await this.updateProject(ticketId, { attachments });
    } catch (error) {
      console.error('Error adding attachment:', error);
      throw error;
    }
  }

  // Remove attachment from ticket
  async removeAttachment(ticketId: string, attachmentId: string): Promise<void> {
    try {
      const ticket = await this.getProjectById(ticketId);
      if (!ticket) throw new Error('Ticket not found');

      const attachments = (ticket.attachments || []).filter(a => a.id !== attachmentId);
      await this.updateProject(ticketId, { attachments });
    } catch (error) {
      console.error('Error removing attachment:', error);
      throw error;
    }
  }

  // Get project count by status
  async getProjectCountByStatus(organizationId: string): Promise<Record<string, number>> {
    try {
      const projects = await this.getProjectsByOrganization(organizationId);
      
      return {
        submitted: projects.filter(p => p.status === 'submitted').length,
        in_progress: projects.filter(p => p.status === 'in_progress').length,
        waiting_customer: projects.filter(p => p.status === 'waiting_customer').length,
        completed: projects.filter(p => p.status === 'completed').length,
        cancelled: projects.filter(p => p.status === 'cancelled').length,
        total: projects.length
      };
    } catch (error) {
      console.error('Error getting project count:', error);
      return {
        submitted: 0,
        in_progress: 0,
        waiting_customer: 0,
        completed: 0,
        cancelled: 0,
        total: 0
      };
    }
  }

  // Helper: Map Firestore document to CustomerTicket
  private mapDocToProject(doc: any): CustomerTicket {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate(),
      cancelledAt: data.cancelledAt?.toDate(),
      dueDate: data.dueDate?.toDate(),
      estimatedCompletionDate: data.estimatedCompletionDate?.toDate(),
      responses: (data.responses || []).map((r: any) => ({
        ...r,
        createdAt: r.createdAt instanceof Timestamp ? r.createdAt.toDate() : new Date(r.createdAt)
      })),
      attachments: data.attachments || []
    } as CustomerTicket;
  }
}

export const customerTicketService = new CustomerTicketService();
